import { useState } from "react";
import useSetErrorMessage from "@atoms/inputs/InputError/hooks/useSetErrorMessage";
import {
  countrySchema,
  designationSchema,
  emailSchema,
  legalConsentSchema,
  nameSchema,
  otherSectorSchema,
  sectorSchema,
  senioritySchema,
} from "@routes/Keyword/templates/AseanInsights/components/DownloadForm/actions/utils/schema.tsx";
import useGetCountries from "@routes/Keyword/templates/AseanInsights/components/DownloadForm/hooks/useGetCountries";
import {
  BUSINESS_SECTORS,
  FORM_DROPDOWN_FOCUS,
  FORM_INPUT_PLACEHOLDER,
  FORM_VARIANT_CLASS_NAME,
  LEGAL_CONSENT,
  NEWSLETTER_SIGNUP_CONSENT,
  SENIORITY_LEVELS,
} from "@routes/Keyword/templates/AseanInsights/components/DownloadForm/utils/contants";
import z from "zod";

type UseGetFormFieldProps = {
  /** The error return from zod schema validation. */
  error: Record<string, string[]>;
};

/** Hook to get the form fields for the Asean Intelligence form. */
const useGetFormField = ({ error }: UseGetFormFieldProps) => {
  const [isDisplayOtherSector, setIsDisplayOtherSector] = useState(false);
  const { handleSetDataError, fieldsErrors } = useSetErrorMessage({ error });
  const countries = useGetCountries();

  const onChangeHandler = (fieldName: string, schema: z.ZodSchema) => {
    return (value: unknown) => {
      if (typeof value !== "string") return;

      handleSetDataError(fieldName, schema, value);

      if (fieldName !== "sector") return;
      if (value === "OTHERS") {
        setIsDisplayOtherSector(true);

        return;
      }

      setIsDisplayOtherSector(false);
    };
  };

  const onCheckboxChangeHandler = (fieldName: string, schema: z.ZodSchema) => {
    return (value: unknown) => {
      if (typeof value !== "string") return;

      const checkValue = value === "on" ? true : false;
      handleSetDataError(fieldName, schema, checkValue);
    };
  };

  const formFields: FormFields[] = [
    {
      type: "text",
      name: "name",
      placeholder: "Name*",
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        placeholder: FORM_INPUT_PLACEHOLDER,
        container: "order-1",
      },
      onHandleChange: onChangeHandler("name", nameSchema),
    },
    {
      type: "email",
      name: "email",
      placeholder: "Company Email Address*",
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        placeholder: FORM_INPUT_PLACEHOLDER,
        container: "order-2",
      },
      onHandleChange: onChangeHandler("email", emailSchema),
    },
    {
      type: "dropdown",
      name: "sector",
      placeholder: "Business Sector*",
      options: BUSINESS_SECTORS,
      isDisplaySearch: true,
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        focus: FORM_DROPDOWN_FOCUS,
        container: "order-3",
      },
      onHandleChange: onChangeHandler("sector", sectorSchema),
    },
    ...(isDisplayOtherSector
      ? [
          {
            type: "text",
            name: "otherSector",
            placeholder: "Please specify*",
            variantClassName: {
              ...FORM_VARIANT_CLASS_NAME,
              placeholder: FORM_INPUT_PLACEHOLDER,
              container: "order-4 lg:order-5 lg:col-span-2",
            },
            onHandleChange: onChangeHandler("otherSector", otherSectorSchema),
          } satisfies FormFields,
        ]
      : []),
    {
      type: "dropdown",
      name: "seniority",
      placeholder: "Seniority*",
      options: SENIORITY_LEVELS,
      isDisplaySearch: false,
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        focus: FORM_DROPDOWN_FOCUS,
        container: "order-5 lg:order-4",
      },
      onHandleChange: onChangeHandler("seniority", senioritySchema),
    },
    {
      type: "text",
      name: "designation",
      placeholder: "Designation*",
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        placeholder: FORM_INPUT_PLACEHOLDER,
        container: "order-6",
      },
      onHandleChange: onChangeHandler("designation", designationSchema),
    },
    {
      type: "dropdown",
      name: "country",
      placeholder: "Country*",
      options: countries,
      isDisplaySearch: true,
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        focus: FORM_DROPDOWN_FOCUS,
        container: "order-7",
      },
      onHandleChange: onChangeHandler("country", countrySchema),
    },
    {
      type: "checkbox",
      name: "legalConsent",
      label: LEGAL_CONSENT,
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        container: "order-8 lg:col-span-2 mt-1",
      },
      onHandleChange: onCheckboxChangeHandler(
        "legalConsent",
        legalConsentSchema
      ),
    },
    {
      type: "checkbox",
      name: "newsletterConsent",
      label: NEWSLETTER_SIGNUP_CONSENT,
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        container: "order-9 lg:col-span-2 w-full mt-1",
      },
    },
    {
      type: "submit",
      name: "submit",
      label: "Download",
      variantClassName: {
        ...FORM_VARIANT_CLASS_NAME,
        container: "order-10 lg:col-span-2",
      },
    },
  ];

  return { formFields, fieldsErrors };
};

export default useGetFormField;
